<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import { EVENT_TYPE, GAME_TYPE, SEGMENT_TRACK_EVENTS } from "~/constants";
import formatDate from "~/utils/format-date";

export interface EventInfoProps {
  event: IModelEvent;
}

const segment = useSegment();
const { t, locale } = useI18n();
const appConfig = useRuntimeConfig().public;
const props = defineProps<EventInfoProps>();
const breakpoints = useBreakpoints(breakpointsTailwind);
const greaterThanXl = breakpoints.greater("xl");
const isLeaguesView = appConfig.APP_CUSTOMER.enable_leagues_view;
const hasViews = appConfig.APP_FLAGS.hasViews;
const getBadgeCategoryPath = (category: IModelIdentityItem) => {
  const mainCategory = category.identityIds ? category.identityIds[0] : { id: null };
  // NOTE: Sub category might not have main category
  // In such case the `id` will be `null`

  if (isLeaguesView) {
    return { path: `/league/${mainCategory.id}`, query: { "round-id": category.id } };
  }
  return `/category/${mainCategory.id}/sub-category/${category.id}`;
};

const onBadgeClick = (item: IModelIdentityItem) => {
  if (isLeaguesView) {
    segment.track(SEGMENT_TRACK_EVENTS.CLICK_ON_LEAGUE, {
      league_id: item.id,
      league_name: item.title,
    });
  }
};

const eventDate = computed(() => formatDate(props.event.gameStartDate, "PPPp", locale.value as TAvailableLocale));
const isClip = computed(() => props.event.eventType === EVENT_TYPE.USER_CLIP);
const isHighlight = computed(() => props.event.eventType === EVENT_TYPE.USER_HIGHLIGHT || props.event.eventType === EVENT_TYPE.HIGHLIGHT);
const isPracticeGameType = computed(() => props.event.gameType === GAME_TYPE.TRAINING || Boolean(props.event.team1Name && !props.event.team2Name));
const isOtherGameType = computed(() => props.event.gameType === GAME_TYPE.OTHER || Boolean(!props.event.team1Name && !props.event.team2Name));
</script>

<template>
  <div class="background-main">
    <EventMetaInfo
      class="px-4 py-2 !mt-0"
      :title="props.event.title"
      :team-name="props.event.team1Name"
      :sport-type="props.event.sportType"
      :is-clip="isClip"
      :is-highlight="isHighlight"
      :is-practice-game-type="isPracticeGameType"
      :is-other-game-type="isOtherGameType"
      :gender="props.event.gender"
    />

    <UDivider lighter />

    <div class="flex flex-wrap justify-between px-4 pt-2 xl:pb-6">
      <div class="flex justify-between w-full">
        <BlockTeamsInfo
          class="mt-2 mb-4"
          :team1-name="props.event.team1Name"
          :team2-name="props.event.team2Name"
          :team1-color="props.event.team1Color"
          :team2-color="props.event.team2Color"
          :team1-logo-url="props.event.team1LogoUrl"
          :team2-logo-url="props.event.team2LogoUrl"
          :title="props.event.title"
          :game-type="props.event.gameType"
        />
        <div v-if="greaterThanXl" class="flex-wrap items-start mt-1 xl:flex gap-x-2 gap-y-4">
          <EventInfoActionButtons :item="props.event" />
        </div>
      </div>

      <div>
        <p class="text-secondary text-caption-medium">
          <!-- TODO: Pass views count instead of zero. -->
          {{ eventDate }} <span v-if="hasViews"> - {{ t('labels.views', 0) }}</span>
        </p>

        <EventInfoDescription v-if="props.event.eventDescription" :description="props.event.eventDescription" />

        <div v-if="props.event.categories.length" class="flex flex-wrap mt-4 gap-y-2">
          <BaseButton
            v-for="category in props.event.categories"
            :key="category.id"
            :to="getBadgeCategoryPath(category)"
            :name="category.title"
            tag="nuxt-link" class="mr-4" link @click.capture="() => onBadgeClick(category)"
          >
            <BaseChip class="bg-[var(--base-category-chip-bgColor)]">
              {{ category.title }}
            </BaseChip>
          </BaseButton>
        </div>
      </div>
    </div>

    <div v-if="!greaterThanXl" class="flex flex-wrap items-start px-2 pt-4 pb-6 gap-x-2 gap-y-4">
      <EventInfoActionButtons :item="props.event" />
    </div>
  </div>
</template>
